import { useState } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import Button from '../../../components/Button';
import { useNotification } from '../../../context/notificationContext';
import Section from '../../../components/Section';
import NamedItem from '../../../components/NamedItem';
import { useOverlay } from '../../../context/overlayContext';
import { markPaymentAsFulfilledService } from '../../../services/portalServices';
import type {
  ClaimKey,
  ClaimcaseKey,
  PaymentKey,
  TransactionDay,
} from '../../../client/claims/types.gen';

type FulfillPaymentProps = {
  claimcaseKey: ClaimcaseKey;
  claimKey: ClaimKey;
  getPayments: () => void;
  partnerApiUrl: string;
  paymentKey: PaymentKey;
};

const FulfillPayment = ({
  partnerApiUrl,
  claimcaseKey,
  claimKey,
  paymentKey,
  getPayments,
}: FulfillPaymentProps) => {
  const { addNotification, removeNotificationWithDelay } = useNotification();
  const { clearChildNodes } = useOverlay();

  const [state, setState] = useState<{
    transactionDate: Date | null;
    isPosting: boolean;
  }>({
    transactionDate: new Date(),
    isPosting: false,
  });

  const handleFulfillPaymentClick = async () => {
    setState(state => ({
      ...state,
      isPosting: true,
    }));

    // The API wants the day as yyyy-MM-dd, which is what the sv-SE short
    // date style produces. This is the one value here that comes from the
    // user (the date picker) rather than from the API.
    const transactionDay: TransactionDay =
      state.transactionDate?.toLocaleString('sv-SE', {
        dateStyle: 'short',
      }) ?? '';

    try {
      await markPaymentAsFulfilledService(
        partnerApiUrl,
        claimcaseKey,
        claimKey,
        paymentKey,
        transactionDay
      );

      removeNotificationWithDelay(
        addNotification('Payment has been marked as fulfilled.', 'success')
      );
    } catch (error) {
      removeNotificationWithDelay(
        addNotification((error as { message: string }).message, 'alert')
      );
    } finally {
      getPayments();
      setState(state => ({
        ...state,
        isPosting: false,
      }));
      clearChildNodes();
    }
  };

  return (
    <Section>
      <header>
        <h3>Mark payment as fulfilled</h3>
      </header>
      <ContentWrapper>
        <NamedItem>
          <label>Due data</label>
          <DatePicker
            className='default'
            locale='sv'
            dateFormat='yyyy-MM-dd'
            selected={state.transactionDate}
            disabled={state.isPosting}
            maxDate={new Date()}
            onChange={date =>
              setState(state => ({
                ...state,
                transactionDate: date,
              }))
            }
          />
        </NamedItem>
        <ButtonWrapper>
          <Button
            variant='secondary'
            disabled={state.isPosting}
            onClick={clearChildNodes}
          >
            Cancel
          </Button>
          <Button
            disabled={state.isPosting}
            onClick={handleFulfillPaymentClick}
          >
            Fulfill payment
          </Button>
        </ButtonWrapper>
      </ContentWrapper>
    </Section>
  );
};

export default FulfillPayment;

const ContentWrapper = styled.main`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-left: 0;
  background-color: #fff;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;
